import { useMemo } from "react";

import { useCurrentPurchaseIntent } from "@/features/my/checkout/shell";

import { type PresaleCart, readPresaleCart } from "../_libs/presale-intent";

/**
 * The reservation this step is confirming — the server's own priced cart.
 *
 * ONE resource backs the whole checkout page: `payload.priceDetail` is the ledger,
 * `payload.checkoutLineItems` is the cart, and `flow` / `accountId` / `placeId` are the
 * reservation's own facts. The funnel used to rebuild all of that from the checkout-link
 * catalog, which meant re-implementing Stripe's coupon math in the browser and quoting a
 * total `POST /checkout/pay` might not agree with.
 *
 * The read is not free of side effects, and that is accepted: `getPurchaseIntentById`
 * mints a Stripe SetupIntent when the account has no payment method, and back-fills
 * `payload.paymentMethodId` from the account default (a DB write). See `_docs/PRESALE.md`.
 *
 * Reuses the member checkout's read rather than adding a second one — both funnels answer
 * to the same row, and a second reader would be a second owner of its cache key, which the
 * shared `useUpdatePurchaseIntent`'s invalidation would then miss.
 */
export function usePresaleCart(purchaseIntentId: string | undefined): {
  cart: PresaleCart | undefined;
  isLoading: boolean;
  /**
   * A re-price is in flight — the ledger on screen is superseded but not yet replaced.
   *
   * Distinct from `isLoading`, which is the first load only. Every write to the intent
   * invalidates it, so this is what covers a promo apply or a card switch settling.
   */
  isFetching: boolean;
  isError: boolean;
} {
  const query = useCurrentPurchaseIntent({
    // Without this the hook falls back to "the account's single pending intent", which for
    // a funnel that always carries its own id would mean reading someone else's checkout.
    enabled: Boolean(purchaseIntentId),
  });

  // Memoized because the page's analytics payload is built from it, and that payload sits
  // in `handlePaymentSuccess`'s `useCallback` deps — which `PaymentStatusModal` keeps in
  // its 1500ms timer's effect deps. A fresh identity every render restarts that timer, and
  // neither the Subscribe event nor the navigation would ever fire.
  const cart = useMemo(() => readPresaleCart(query.data), [query.data]);

  return {
    cart,
    // Gated on the id as well as the query — the same shape `usePresaleOfferTarget` uses.
    // A disabled query stays `pending` forever, so anything reading its status directly
    // (rather than `isLoading`, which v5 also requires `isFetching` for) would hang the
    // page on a visit that carries no reservation at all.
    isLoading: Boolean(purchaseIntentId) && query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}
